import {agentTaskQueryKey} from '@github-ui/agent-sessions/hooks/use-task'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import type {Session} from '@github-ui/agent-sessions/types/session'
import {useEnvironment} from '@github-ui/agent-sessions/hooks/use-environment'
import {useTaskStartup} from '@github-ui/agent-sessions/ahp/use-relay-provisioning'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {skipToken, useQuery} from '@github-ui/react-query'
import {useSearchParams} from '@github-ui/use-navigate'

import type {NormalizedCopilotAgentsTaskPayload} from '../types/copilot-agents-task-payload'
import {
  resolveWorkbenchEnvironmentKind,
  resolveWorkbenchOwner,
  resolveWorkbenchSession,
} from './agents-workbench-eligibility'
import {useRoutedTaskHandoff} from './use-routed-task-handoff'

export function useAgentsWorkbench(
  payload: NormalizedCopilotAgentsTaskPayload,
  repository?: {repoOwner: string; repoName: string},
) {
  const taskId = payload.taskId ?? payload.task.id
  // TaskChat owns fetching; this sibling observes its authoritative task updates.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Disabled cache observer; it never fetches or persists API responses.
  const {data: task = payload.task} = useQuery<HydratedAPITask>({
    queryKey: agentTaskQueryKey(repository?.repoOwner, repository?.repoName, taskId),
    queryFn: skipToken,
    enabled: false,
    initialData: {...payload.task, sessions: payload.sessions},
  })
  // Observe the sessions useSessions already resolved. A deep-linked session_id can be
  // absent from the task payload's array, in which case useSessions fetches it directly,
  // so reading task.sessions here would miss it.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Disabled cache observer; it never fetches or persists API responses.
  const {data: resolvedSessions} = useQuery<Session[]>({
    queryKey: ['agent-sessions', repository?.repoOwner, repository?.repoName, taskId],
    queryFn: skipToken,
    enabled: false,
  })
  const [searchParams] = useSearchParams()
  const selectedSessionId = searchParams.get('session_id')
  const session = resolveWorkbenchSession(resolvedSessions ?? task.sessions, selectedSessionId)
  const boundEnvironmentId = session?.environment_id
  const {data: environment} = useEnvironment(boundEnvironmentId, {
    enabled: !!boundEnvironmentId && task.current_environment?.id !== boundEnvironmentId,
  })
  const {persistentChat, isProvisioningThisTask} = useRoutedTaskHandoff(taskId)
  const startup = useTaskStartup(taskId)
  const launch =
    startup?.launch &&
    (!boundEnvironmentId || !startup.launch.environmentId || startup.launch.environmentId === boundEnvironmentId)
      ? startup.launch
      : undefined
  const environmentKind =
    selectedSessionId && !session?.environment_id
      ? undefined
      : resolveWorkbenchEnvironmentKind({
          taskEnvironment: task.current_environment,
          boundEnvironmentId,
          resolvedEnvironment: environment,
          launchEnvironmentKind: isProvisioningThisTask ? persistentChat?.launchEnvironmentKind : launch?.kind,
        })

  return {
    environmentKind,
    enabled: copilotFeatureFlags.quickChatTaskViewEnabled && environmentKind === 'managed-sandbox',
    task,
    ownerLogin: resolveWorkbenchOwner(
      task,
      repository?.repoOwner,
      isProvisioningThisTask ? persistentChat?.launchOwnerLogin : launch?.ownerLogin,
    ),
  }
}
